import { Link } from 'react-router-dom'
import { usePropertyStats, useContactStats, useBuildingStats } from '@/hooks'

/**
 * Página del panel de control que muestra estadísticas generales y acciones rápidas.
 */
export function Dashboard() {
  const { data: statsData, isLoading: propertiesLoading, error } = usePropertyStats()
  const { data: contactStatsData } = useContactStats()
  const { data: buildingStatsData, isLoading: buildingsLoading } = useBuildingStats()

  const stats = {
    totalProperties: statsData?.total ?? 0,
    publishedProperties: statsData?.published ?? 0,
    draftProperties: statsData?.draft ?? 0,
    archivedProperties: statsData?.archived ?? 0,
  }

  const buildingStats = {
    total: buildingStatsData?.total ?? 0,
    published: buildingStatsData?.published ?? 0,
    draft: buildingStatsData?.draft ?? 0,
    archived: buildingStatsData?.archived ?? 0,
  }

  const contactStats = {
    total: contactStatsData?.total ?? 0,
    unread: contactStatsData?.unread ?? 0,
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Panel de Control</h1>
        <p className="mt-2 text-muted-foreground">
          Bienvenido al panel de administración de A1Homes.
        </p>
      </div>

      {/* Estadísticas de Propiedades */}
      <div>
        <h2 className="mb-4 text-xl font-semibold">Propiedades</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total de Propiedades"
            value={stats.totalProperties}
            description="Todas las propiedades en el sistema"
            isLoading={propertiesLoading}
          />
          <StatCard
            title="Publicadas"
            value={stats.publishedProperties}
            description="Visibles en el sitio web"
            className="border-green-200 dark:border-green-800"
            isLoading={propertiesLoading}
          />
          <StatCard
            title="Borradores"
            value={stats.draftProperties}
            description="Aún no publicadas"
            className="border-yellow-200 dark:border-yellow-800"
            isLoading={propertiesLoading}
          />
          <StatCard
            title="Archivadas"
            value={stats.archivedProperties}
            description="Ocultas al público"
            className="border-gray-200 dark:border-gray-700"
            isLoading={propertiesLoading}
          />
        </div>
      </div>

      {/* Estadísticas de Edificios */}
      <div>
        <h2 className="mb-4 text-xl font-semibold">Edificios</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total de Edificios"
            value={buildingStats.total}
            description="Todas las promociones en el sistema"
            isLoading={buildingsLoading}
          />
          <StatCard
            title="Publicados"
            value={buildingStats.published}
            description="Visibles en el sitio web"
            className="border-green-200 dark:border-green-800"
            isLoading={buildingsLoading}
          />
          <StatCard
            title="Borradores"
            value={buildingStats.draft}
            description="Aún no publicados"
            className="border-yellow-200 dark:border-yellow-800"
            isLoading={buildingsLoading}
          />
          <StatCard
            title="Archivados"
            value={buildingStats.archived}
            description="Ocultos al público"
            className="border-gray-200 dark:border-gray-700"
            isLoading={buildingsLoading}
          />
        </div>
      </div>

      {/* Contact Stats */}
      <div className="grid gap-4 md:grid-cols-2">
        <Link
          to="/contact"
          className="block rounded-lg border bg-card p-6 text-card-foreground shadow-sm transition-colors hover:border-primary"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold">{contactStats.total}</div>
              <div className="font-medium">Mensajes de Contacto</div>
              <div className="mt-1 text-sm text-muted-foreground">Total recibidos</div>
            </div>
            {contactStats.unread > 0 && (
              <div className="flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                <span className="flex h-2 w-2 rounded-full bg-amber-500" />
                <span className="text-sm font-medium">{contactStats.unread} sin leer</span>
              </div>
            )}
          </div>
        </Link>
      </div>

      {/* Error de estadísticas */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400">
          Error al cargar estadísticas. {error instanceof Error ? error.message : 'Por favor, inténtalo de nuevo.'}
        </div>
      )}

      {/* Acciones rápidas */}
      <div>
        <h2 className="mb-4 text-xl font-semibold">Acciones Rápidas</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <QuickActionCard
            title="Crear Propiedad"
            description="Añadir un nuevo listado de propiedad"
            href="/properties/create"
          />
          <QuickActionCard
            title="Crear Edificio"
            description="Añadir una nueva promoción"
            href="/buildings/create"
          />
          <QuickActionCard
            title="Ver Propiedades"
            description="Gestionar listados de propiedades existentes"
            href="/properties"
          />
          <QuickActionCard
            title="Ver Edificios"
            description="Gestionar promociones existentes"
            href="/buildings"
          />
          <QuickActionCard
            title="Ver Mensajes"
            description="Gestionar consultas de contacto"
            href="/contact"
          />
        </div>
      </div>

      {/* Marcador de actividad reciente */}
      <div>
        <h2 className="mb-4 text-xl font-semibold">Actividad Reciente</h2>
        <div className="rounded-lg border bg-card p-6 text-card-foreground">
          <p className="text-sm text-muted-foreground">
            La actividad reciente se mostrará aquí una vez que se complete la integración del registro de auditoría.
          </p>
        </div>
      </div>
    </div>
  )
}

interface StatCardProps {
  title: string
  value: number
  description: string
  className?: string
  isLoading?: boolean
}

function StatCard({ title, value, description, className = '', isLoading = false }: StatCardProps) {
  return (
    <div className={`rounded-lg border bg-card p-6 text-card-foreground shadow-sm ${className}`}>
      <div className="text-2xl font-bold">
        {isLoading ? (
          <div className="h-8 w-12 animate-pulse rounded bg-muted" />
        ) : (
          value
        )}
      </div>
      <div className="font-medium">{title}</div>
      <div className="mt-1 text-sm text-muted-foreground">{description}</div>
    </div>
  )
}

interface QuickActionCardProps {
  title: string
  description: string
  href: string
}

function QuickActionCard({ title, description, href }: QuickActionCardProps) {
  return (
    <Link
      to={href}
      className="block rounded-lg border bg-card p-6 text-card-foreground shadow-sm transition-colors hover:border-primary hover:bg-accent"
    >
      <div className="font-semibold">{title}</div>
      <div className="mt-1 text-sm text-muted-foreground">{description}</div>
    </Link>
  )
}
